import {AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, Optional, Output, ViewChild, ViewEncapsulation} from '@angular/core';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';
import {TRANSLATION_EDITOR_SCOPE} from '../../../constants';
import {KEYS} from '../../../translation-keys';
import {FroalaTemplateVariablesService} from '../../froala/froala-template-variables.service';


// hack to inject decorator declarations. must occur before class declaration!
export interface QuestionIndexComponent extends Translatable {}
declare const $: any;

@Component({
  selector: 'app-qa-question-index',
  templateUrl: './question-index.component.html',
  styleUrls: ['./question-index.component.css'],
  encapsulation: ViewEncapsulation.None
})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class QuestionIndexComponent implements AfterViewInit {
  @Output() done: EventEmitter<any> = new EventEmitter();
  @Output() jump: EventEmitter<number> = new EventEmitter();
  @Input() questionnaire: Questionnaire;
  @Input() mappings: any = {};
  @Input() pages: {startIndex: number; endIndex: number; title: string}[] = [];
  @Input() pageViewEnabled = false;
  @Input() currentPageIndex = 0;
  @Input() locale = 'de_DE';

  @ViewChild('searchInput') private searchInput: ElementRef<HTMLInputElement>;

  /**
   * Search term
   */
  public query = '';

  /**
   * Plain text titles by question id, html is stripped once
   */
  private plainTitles: {[id: string]: string} = {};

  /**
   * Readable names of template variables by path, resolved once per opened picker
   */
  private variableLabels: {[path: string]: string} = null;

  /**
   * Injects dependencies
   */
  public constructor(
    @Optional() private translationService: TranslationService,
    @Optional() private templateVariables: FroalaTemplateVariablesService
  ) {
  }

  /**
   * Focuses the search field when the picker opens
   */
  public ngAfterViewInit(): void {
    setTimeout(() => this.searchInput && this.searchInput.nativeElement.focus(), 50);
  }

  /**
   * Returns the elements to show, grouped by page in the page view and filtered by the search term
   *
   * @returns {{title: string, current: boolean, items: any[]}[]}
   */
  public getGroups(): {title: string; current: boolean; items: {index: number; title: string; type: string; icon: string}[]}[] {
    const questions = (this.questionnaire && this.questionnaire.questions) || [];
    const query = this.query.trim().toLowerCase();
    const usePages = this.pageViewEnabled && this.pages && this.pages.length > 1;
    const ranges = usePages ? this.pages : [{startIndex: 0, endIndex: questions.length - 1, title: ''}];

    const groups = [];
    ranges.forEach((page, pageIndex) => {
      const items = [];
      for (let index = page.startIndex; index <= page.endIndex; index++) {
        const question = questions[index];
        if (!question || (usePages && question.format === 'pageBreak')) {
          continue;
        }
        const item = this.toItem(question, index);
        if (query && `${index + 1} ${item.title} ${item.type}`.toLowerCase().indexOf(query) === -1) {
          continue;
        }
        items.push(item);
      }
      if (!items.length) {
        return;
      }
      const pageTitle = usePages ? `${this._(KEYS.EDITOR.PAGE)} ${pageIndex + 1}${page.title ? ': ' + page.title : ''}` : '';
      groups.push({title: pageTitle, current: usePages && pageIndex === this.currentPageIndex, items});
    });
    return groups;
  }

  /**
   * Jumps to the given element
   *
   * @param {number} index
   */
  public onJump(index: number): void {
    this.jump.emit(index);
    this.done.emit();
  }

  /**
   * Jumps to the first element matching the search term
   */
  public onJumpToFirstMatch(): void {
    const first = this.getGroups()[0];
    first && first.items.length && this.onJump(first.items[0].index);
  }

  public trackGroup(index: number, group: {title: string}): string {
    return group.title || String(index);
  }

  public trackItem(index: number, item: {index: number}): number {
    return item.index;
  }

  /**
   * Builds the list entry of an element
   */
  private toItem(question: Questionnaire.Container, index: number): {index: number; title: string; type: string; icon: string} {
    const mapping = (question.extra && this.mappings[`${question.format}#${question.extra}`]) || this.mappings[question.format] || {};
    return {
      index,
      title: this.getPlainTitle(question),
      type: mapping.title ? this._(mapping.title) : question.format,
      icon: mapping.icon || 'fa-square-o'
    };
  }

  /**
   * Returns the question title without markup
   */
  private getPlainTitle(question: Questionnaire.Container): string {
    const raw = (question.title && question.title[this.locale]) || '';
    const cacheKey = `${question.id}:${raw}`;
    if (this.plainTitles[cacheKey] === undefined) {
      const element = document.createElement('div');
      element.innerHTML = raw;
      this.plainTitles[cacheKey] = this.readableTemplate(element.textContent || '').replace(/\s+/g, ' ').trim();
    }
    return this.plainTitles[cacheKey];
  }

  /**
   * Replaces template placeholders with the readable variable names known from the editor
   * and drops condition markers, so titles read like the preview
   *
   * @param {string} text
   * @returns {string}
   */
  private readableTemplate(text: string): string {
    if (text.indexOf('{{') === -1) {
      return text;
    }
    if (this.variableLabels === null) {
      this.variableLabels = this.templateVariables ? this.templateVariables.getLabels(this.locale) : {};
    }
    return text
      .replace(/\{\{\s*else\s*\}\}/g, ' / ')
      .replace(/\{\{\s*[#\/][^}]*\}\}/g, ' ')
      .replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_.-]*)\s*\}\}/g, (match, path) => {
        const canonical = path.replace(/^patient\./, 'client.');
        return this.variableLabels[canonical] || path;
      });
  }

  /**
   * Sadly, NgbPopover does not support auto hide on click outside, so we have to implement this.
   *
   * @param targetElement
   */
  @HostListener('document:click', ['$event.target'])
  public onClick(targetElement) {
    const popover = $('ngb-popover-window');
    const clickInside = popover.find(targetElement).length;

    if (!clickInside) {
      $('ngb-popover-window').hide();
    }
  }
}
